import {
    Alert,
    alpha,
    Box,
    Button,
    ButtonGroup,
    Container,
    Grid,
    MenuItem,
    Paper,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { Tabs, Tab } from '@mui/material';
import {
    LinkOff,
    NavigateBefore,
    NavigateNext,
    OpenInNew,
} from '@mui/icons-material';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    useApproveReservationMutation,
    useEditReservationMutation,
    useGetAvailabilityQuery,
    useGetExpenseClaimsQuery,
    useGetIndividualAssetQuery,
    useGetJobsQuery,
    useGetReservationConflictsMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import LoadingComponent from '../../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';
import { theme } from '../../../../shared/theme';
import UserRole from '../../../../shared/AuthRoles';
import { Status } from '../../../enums/booking';
import toast from '../../../shared/Toast';
import CancelReservationModal from './CancelReservationModal';
import ContactForm from './ContactForm';
import NirovisonAccessModal from './NirovisionAccessModal';
import Conflicts from './Conflicts';
import ReservationExpenseClaims from '../ExpenseClaims/ReservationExpenseClaims';
import { camelCaseToTitleCase, capitalizeName } from '../../../../shared/utils';
import ReservationHostsContractors from '../ReservationContractors/ReservationHostsContractors';
import ReservationAttachmentsCard from '../ReservationAttachments/ReservationAttachmentsCard';
import DeclineReservationModal from './DeclineReservationModal';
import ReservationSummarySection from './SharedReseravtionComponents/ReservationSummarySection';
import ReservationDetailsSection, {
    LinkSx,
} from './SharedReseravtionComponents/ReservationDetailsSection';
import ExperienceInfoSection from './SharedReseravtionComponents/ExperienceInfoSection';
import ReservationItinerarySection from './SharedReseravtionComponents/ReservationItinerarySection';
import ReservationGuestsSection from './SharedReseravtionComponents/ReservationGuestsSection';
import ReservationChargesSection from './SharedReseravtionComponents/ReservationChargesSection';
import GuestPaymentHistoryAccordion from './SharedReseravtionComponents/GuestPaymentHistoryAccordion';
import ReservationHistory from './SharedReseravtionComponents/ReservationHistory';
import ReservationNotesAccordion from './SharedReseravtionComponents/ReservationNotesAccordion';
import RsvpModal from './RsvpModal';
import RsvpActions from './RsvpActions';
import { BookingAttendee } from '../../../types/reservation';
import ItineraryTab from './Itinerary/ItineraryTab';
import AddOnSection from './Addons/AddOnSection';
import ReservationSpacesSection from './SharedReseravtionComponents/ReservationSpacesSection';
import { useParamState } from '../../../hooks/useParamState';
import { z } from 'zod';
import { AddOn } from '../../../types/experience';
import { trackEvent } from '../../../../app/Analytics';
import { BookingSetMenu } from '../../../types/set-menu';
import { ServiceType, Status as JobStatus } from '../../../enums/contractor';
import { shouldShowOptionalServicesSection } from '../EditReservation/optionalServiceAvailability';
import { ExperienceType } from '../../ExperienceReviews/ReviewWizard';

const viewKeys = [
    'details',
    'property',
    'guests',
    'itinerary',
    'reservationSpaces',
    'charges',
    'payments',
    'history',
] as const;

export type ViewKey = (typeof viewKeys)[number];

type Props = {
    reservation?: any;
    asset?: any;
    variant?: 'view' | 'submitted';
};

export default function ViewIndividualReservation(params: any) {
    const [tab, setTab] = useParamState<ViewKey>('tab', {
        type: z.enum(viewKeys),
        defaultValue: 'details',
    });

    // the tab value when add on button is clicked. we will return to this tab if modal is closed without selecting an add on.
    const [originalTab, setOriginalTab] = useState<ViewKey>('details');

    const reservationFromProps =
        (params as Props)?.reservation ?? params?.reservation;
    const assetFromProps = (params as Props)?.asset ?? params?.asset;
    const variant: 'view' | 'submitted' =
        (params as Props)?.variant ?? params?.variant ?? 'view';

    const isSubmitted = variant === 'submitted';
    const [editReservation] = useEditReservationMutation();

    const [open, setOpen] = useState(false);
    const loggedInUser = UserRole();
    const isOwner = loggedInUser?.role?.includes('owner');
    const isGuest = loggedInUser?.role?.includes('guest');
    const navigate = useNavigate();
    const [openNirovisionModal, setOpenNirovisionModal] = useState(false);
    const [showItineraryForm, setShowItineraryForm] = useState(false);
    const [isAddOnForm, setIsAddOnForm] = useState(false);
    const [preselectedAddOn, setPreselectedAddOn] = useState<{
        id: number;
        type: string;
    } | null>(null);
    const [preselectedPartnerService, setPreselectedPartnerService] =
        useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const shouldOpenAccessRequests = searchParams.get('accessRequests') === '1';

    const getReservationDetails = reservationFromProps;
    const isOwnerBooker = getReservationDetails?.bookerType?.includes('owner');
    const isOnBehalf = getReservationDetails?.onBehalf;

    const loggedInAttendee =
        getReservationDetails?.staffAttending?.find(
            ({ id }: BookingAttendee) => id === loggedInUser.sub,
        ) ??
        getReservationDetails?.externalAttendees?.find(
            ({ id }: BookingAttendee) => id === loggedInUser.sub,
        );

    const isPrimaryGuestOrBooker = [
        getReservationDetails?.booker?.id,
        getReservationDetails?.createdBy?.id,
    ].includes(loggedInUser?.sub);

    const canAllocateRooms =
        (!isOwner || isOnBehalf) && (!isGuest || isPrimaryGuestOrBooker);
    const [openRsvpModal, setOpenRsvpModal] = useState(false);

    useEffect(() => {
        if (!shouldOpenAccessRequests) return;
        setOpenNirovisionModal(true);
    }, [shouldOpenAccessRequests]);

    const handleCloseNirovisionModal = () => {
        setOpenNirovisionModal(false);
        if (searchParams.has('accessRequests')) {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete('accessRequests');
                    return next;
                },
                { replace: true },
            );
        }
    };

    const isMobile = useMediaQuery('(max-width: 770px)');
    const isLaptop = useMediaQuery('(max-width: 1200px)');
    const isNavSelectLayout = useMediaQuery(theme.breakpoints.down('md'));

    const [getConflicts] = useGetReservationConflictsMutation();
    const [conflicts, setConflicts] = useState<any[]>([]);
    const [openDeclineModal, setOpenDeclineModal] = useState(false);

    const location = useLocation();
    const index = location?.state?.index;

    const reservations = location?.state?.reservations;
    const next = reservations?.[index + 1];
    const prev = reservations?.[index - 1];

    const { data: jobs } =
        useGetJobsQuery(
            { bookingId: getReservationDetails?.id },
            { skip: !getReservationDetails?.id },
        ) || [];

    const massageJobs = (jobs || [])?.filter(
        (job) =>
            job.serviceType === ServiceType.Wellness &&
            ![JobStatus.Cancelled, JobStatus.Declined].includes(job.status),
    );
    const cateringJobs = (jobs || [])?.filter(
        (job) =>
            job.serviceType === ServiceType.Catering &&
            ![JobStatus.Cancelled, JobStatus.Declined].includes(job.status),
    );
    const alcoholJobs = (jobs || [])?.filter(
        (job) =>
            job.serviceType === ServiceType.Alcohol &&
            ![JobStatus.Cancelled, JobStatus.Declined].includes(job.status),
    );
    const setMenuJobs: BookingSetMenu[] = getReservationDetails?.setMenus;
    const cateringSetMenuJobs = setMenuJobs?.filter(
        (setMenuJob) =>
            setMenuJob.serviceType === ServiceType.Catering &&
            ![JobStatus.Cancelled, JobStatus.Declined].includes(
                setMenuJob.status,
            ),
    );
    const alcoholSetMenuJobs = setMenuJobs?.filter(
        (setMenuJob) =>
            setMenuJob.serviceType === ServiceType.Alcohol &&
            ![JobStatus.Cancelled, JobStatus.Declined].includes(
                setMenuJob.status,
            ),
    );

    const hasMassageJobs = (massageJobs?.length || 0) > 0;
    const hasCateringJobs = (cateringJobs?.length || 0) > 0;
    const hasEstimatedJobs = hasMassageJobs || hasCateringJobs;

    const { data: expenseClaims }: any = useGetExpenseClaimsQuery(
        { bookingId: getReservationDetails?.id },
        { skip: !getReservationDetails?.id || isGuest },
    );

    const moduleId = getReservationDetails?.module?.id;
    const isModuleAvailable = getReservationDetails?.module?.isAvailable;

    const shouldFetchAsset = !!moduleId && !assetFromProps && isModuleAvailable;

    const {
        data: fetchedAsset,
        isFetching,
        isError,
    } = useGetIndividualAssetQuery(`modules/${moduleId}`, {
        skip: !shouldFetchAsset,
    });

    const getIndividualAsset = assetFromProps ?? fetchedAsset;

    const shouldShowRoomAllocations = !!getIndividualAsset?.spaces.find(
        (space: any) =>
            space?.sleepingCapacity > 0 && space?.sleepingCapacity > 0,
    );

    const { data: availability } = useGetAvailabilityQuery(
        {
            url: `/modules/commercial/${getIndividualAsset?.id}/get-availability`,
            body: {
                startDate: getReservationDetails?.startDate,
                endDate: getReservationDetails?.endDate,
            },
        },
        {
            skip:
                !getIndividualAsset?.id ||
                !getReservationDetails?.startDate ||
                !getReservationDetails?.endDate,
        },
    );

    const handlePermissionDenied = () => {
        navigate(`/permissionDenied`, {
            state: { name: 'individualReservation' },
        });
    };

    const shouldPermissionDeny =
        isError &&
        (isModuleAvailable === true || isModuleAvailable === undefined);

    useEffect(() => {
        if (!shouldPermissionDeny) return;
        handlePermissionDenied();
    }, [shouldPermissionDeny]);

    const [approveReservation] = useApproveReservationMutation();

    const fullAddress = `${getIndividualAsset?.streetAddress} ${getIndividualAsset?.suburb} ${getIndividualAsset?.postcode} ${getIndividualAsset?.state} ${getIndividualAsset?.countryCode}`;
    const shortAddress = `${getIndividualAsset?.suburb} ${getIndividualAsset?.postcode} ${getIndividualAsset?.state} ${getIndividualAsset?.countryCode}`;
    const fullAddressDropoff = `${getIndividualAsset?.details?.dropoffStreetAddress} ${getIndividualAsset?.details?.dropoffSuburb} ${getIndividualAsset?.details?.dropoffPostcode} ${getIndividualAsset?.details?.dropoffState} ${getIndividualAsset?.details?.dropoffCountryCode}`;

    const formatDate = (date: any, format: string = 'DD/MM/YYYY') => {
        return date ? moment(date).format(format) : '';
    };

    const startDate = formatDate(getReservationDetails?.startDate);
    const calendarStart = formatDate(
        getReservationDetails?.startDate,
        'YYYY-MM-DD',
    );
    const calendarEnd = formatDate(
        getReservationDetails?.endDate,
        'YYYY-MM-DD',
    );
    const endDate = formatDate(getReservationDetails?.endDate);
    const createdAtDate = formatDate(
        getReservationDetails?.createdAt,
        'DD/MM/YYYY',
    );

    const moduleTypeDisplay = (() => {
        const type =
            getIndividualAsset?.type ?? getReservationDetails?.module?.type;
        if (!type) return null;

        const subType = getIndividualAsset?.details?.subType;
        if (type === 'commercial' && subType === 'house') {
            return capitalizeName('home office');
        }

        return capitalizeName(subType ?? type);
    })();

    const getBaseDescription = (r: any, s: string, e: string) => {
        return `REF: #${r?.id} - ${window.location.href} [br] [li] Reservation at ${r?.module?.name} [br] [li] Date: ${s}-${e}`;
    };

    const getOwnerNotes = (r: any) => {
        return r?.bookerType === 'owner' && !r?.onBehalf
            ? `[br] [li] Notes: ${r?.notes}`
            : '';
    };

    const getBookingInfo = (r: any) => {
        return r?.bookerType !== 'owner' || r?.onBehalf
            ? `[br] [li] Reserved By: ${r?.booker?.firstName} ${r?.booker?.lastName} - ${r?.booker?.email}`
            : '';
    };

    const getCheckinCheckout = (r: any) => {
        return r?.bookingInfo?.checkinTime
            ? `[br] [li] Checkin From: ${r?.bookingInfo?.checkinTime} until ${r?.bookingInfo?.checkoutTime}`
            : '';
    };

    const getGuestsDetails = (r: any, a: any) => {
        return `${
            r?.bookingInfo?.adults > 0
                ? `[li] No Adults Attending: ${r?.bookingInfo?.adults}`
                : ''
        }
        ${
            r?.bookingInfo?.children > 0
                ? `[li] No Children Attending: ${r?.bookingInfo?.children}`
                : ''
        }
        ${
            a?.details?.subType !== 'office space' &&
            a?.details?.subType !== 'commercial'
                ? `[li] Sleeps: ${a?.details.overnightGuests}`
                : ''
        }
        ${
            a?.details?.subType !== 'office space' &&
            a?.details?.subType !== 'commercial'
                ? `[li] No. Overnight Guests: ${r?.bookingInfo?.overnightGuests}`
                : ''
        }`;
    };

    const getVehicleDescription = (r: any, fa: string, fad: string, a: any) => {
        return ` [br] [li] Reserved ${r?.module?.name}
            [br] [li] Date: ${startDate}-${endDate}  
            [br] [li] Reserved By: ${r?.booker?.firstName} ${r?.booker?.lastName} - ${r?.booker?.email}
            ${getOwnerNotes(r)}
            ${
                !a?.details?.dropoffLocationSameAsPickup
                    ? `[br] [li] Pickup at: ${fa} [br] [li] Dropoff at: ${fad}`
                    : ''
            }`;
    };

    const getActivityDescription = (r: any) => {
        return `REF: #${r?.id} - ${window.location.href} 
            [br] [li]  Reservation at ${r?.module?.name} 
            [br] [li] Date: ${startDate}  
            [br] [li] Reserved By: ${r?.booker?.firstName} ${r?.booker?.lastName} - ${r?.booker?.email}
            ${
                r?.bookerType !== 'owner' || r?.onBehalf
                    ? `[br] [li] No Guests Attending: ${r?.bookingInfo?.guests}`
                    : ''
            }
            [br] [li] Notes: ${
                r?.bookerType === 'owner' && !r?.onBehalf
                    ? r?.notes
                    : r?.bookingInfo?.notes
            }`;
    };

    const getCommercialDescription = (r: any) => {
        const desks = r?.bookingInfo?.desks
            ? `[li] No. Dedicated Desks: ${r?.bookingInfo?.desks} [br] `
            : '';
        const offices = r?.bookingInfo?.privateOffices
            ? `[li] No. Private Offices: ${r?.bookingInfo?.privateOffices} [br] `
            : '';
        const rooms = r?.bookingInfo?.meetingRooms
            ? `[li] No. Meeting Rooms: ${r?.bookingInfo?.meetingRooms} [br] `
            : '';
        const notes = r?.bookingInfo?.notes
            ? `[li] Notes: ${r?.bookingInfo?.notes}`
            : '';
        return `REF: #${r?.id} - ${window.location.href} 
            [br] [li] Booking at ${r?.module?.name} 
            [br] [li] Date: ${startDate}-${endDate} 
            [br] [li] Available From: ${r?.bookingInfo?.checkinTime} until ${r?.bookingInfo?.checkoutTime} 
            [br] [li] Booked By: ${r?.booker?.firstName} ${r?.booker?.lastName} - ${r?.booker?.email} 
            [br] ${desks}${offices}${rooms}${notes}`;
    };

    const calDescriptionNonVehicle = `${getBaseDescription(
        getReservationDetails,
        startDate,
        endDate,
    )}
        ${getOwnerNotes(getReservationDetails)}
        ${getBookingInfo(getReservationDetails)}
        ${getCheckinCheckout(getReservationDetails)}
        ${getGuestsDetails(getReservationDetails, getIndividualAsset)}
        [li] Notes: ${getReservationDetails?.bookingInfo?.notes}`;

    const calDescriptionCommercial = `${getBaseDescription(
        getReservationDetails,
        startDate,
        endDate,
    )}
        ${getOwnerNotes(getReservationDetails)}
        ${getBookingInfo(getReservationDetails)}
        ${getCheckinCheckout(getReservationDetails)}
        ${getCommercialDescription(getReservationDetails)}`;

    const calDescriptionActivity = getActivityDescription(
        getReservationDetails,
    );

    const calDescriptionVehicle = getVehicleDescription(
        getReservationDetails,
        fullAddress,
        fullAddressDropoff,
        getIndividualAsset,
    );

    const navigateToEditReservation = () => {
        trackEvent(
            'Reservations',
            'Edit Reservation',
            `Edit Reservation - (${loggedInUser?.role})`,
        );

        navigate(
            `/editReservation/${getReservationDetails?.module?.name}/${getReservationDetails?.id}`,
            { state: { name: 'editReservation' } },
        );
    };

    const onClickApprove = async () => {
        trackEvent(
            'Reservations',
            'Approve Reservation',
            `Approve Reservation - (${loggedInUser?.role})`,
        );

        const bookingId = getReservationDetails?.id;

        try {
            await approveReservation(bookingId).unwrap();
            toast.success(`Reservation has been approved`);
        } catch (error: any) {
            if (error?.status === 409) {
                toast.error(
                    `Failed to approve reservation: ${error.data?.message}`,
                );
            } else {
                toast.error('Failed to approve reservation');
            }
        }
    };

    const onClickOpenModal = () => setOpen(true);
    const handleCloseModal = () => setOpen(false);

    const onClickOpenDeclineModal = () => {
        trackEvent(
            'Reservations',
            'Decline Reservation',
            `Decline Reservation - (${loggedInUser?.role})`,
        );

        setOpenDeclineModal(true);
    };

    const handleChangeDeclineModal = () => setOpenDeclineModal(false);
    const handleCloseDeclineModal = () => setOpenDeclineModal(false);

    const isResourceExceeded = (bookingValue: number, availableValue: number) =>
        bookingValue > availableValue;

    const isConflict = () => {
        if (getReservationDetails?.module?.type === 'commercial') return false;
        return (
            conflicts.filter(
                (conflict) =>
                    conflict.status === Status.Approved ||
                    conflict.status === Status.Completed,
            ).length > 0
        );
    };

    const disableApprove =
        isResourceExceeded(
            getReservationDetails?.bookingInfo?.desks,
            availability?.desks,
        ) ||
        isResourceExceeded(
            getReservationDetails?.bookingInfo?.privateOffices,
            availability?.privateOffices,
        ) ||
        isResourceExceeded(
            getReservationDetails?.bookingInfo?.meetingRooms,
            availability?.meetingRooms,
        ) ||
        isConflict();

    const fetchData = async () => {
        try {
            const request = { bookingId: getReservationDetails.id };
            const response = await getConflicts(request).unwrap();
            setConflicts(response);
        } catch {
            toast.error('Error fetching conflicting bookings');
        }
    };

    useEffect(() => {
        if (getReservationDetails?.id && loggedInUser?.role.includes('owner'))
            fetchData();
    }, [getReservationDetails?.id, loggedInUser?.role]);

    const statusLower = String(
        getReservationDetails?.status || '',
    ).toLowerCase();
    const isCompleted = statusLower === 'completed';
    const isDeclined = statusLower === 'declined';

    const isCancelled = statusLower === 'cancelled';
    const isPending = statusLower === 'pending';
    const isApproved = statusLower === 'approved';

    const isAttending =
        ((getReservationDetails?.staffAttending?.find(
            (staff: any) => staff.id === loggedInUser?.sub,
        ) &&
            loggedInUser?.sub !== getReservationDetails?.booker?.id &&
            !loggedInUser?.role.includes('admin')) ||
            (getReservationDetails?.guest?.id !== loggedInUser?.company &&
                loggedInUser?.role !== 'admin')) &&
        !loggedInUser?.role.includes('owner');

    const canEdit =
        !isCompleted &&
        !isCancelled &&
        !isDeclined &&
        (isPending || isApproved) &&
        isModuleAvailable;

    const canOwnerAdminApproveDecline =
        !isSubmitted &&
        !isCompleted &&
        loggedInUser?.role === 'owner_admin' &&
        (statusLower === 'pending' || isDeclined);

    const canGuestCancelRequest =
        !isCompleted &&
        statusLower === 'pending' &&
        loggedInUser?.role?.includes('guest');

    const canCancelApproved =
        !isSubmitted &&
        !isCompleted &&
        statusLower === 'approved' &&
        !isDeclined;

    const canOwnerCancelCompleted = false;

    const hasPrimaryActions =
        canEdit ||
        canOwnerAdminApproveDecline ||
        canGuestCancelRequest ||
        canCancelApproved;

    const chargesTabTopRef = useRef<HTMLDivElement | null>(null);
    const [chargesExpanded, setChargesExpanded] = useState(false);
    const [chargesJumpTo, setChargesJumpTo] = useState<boolean | undefined>(
        undefined,
    );

    const canShowGuests = getIndividualAsset?.type !== 'vehicle';
    const canShowCharges = !isOwnerBooker;
    const canShowPayments = !isSubmitted && !isOwnerBooker;

    const canShowContactForm = !isOwnerBooker;

    const openChargesTabFromDetails = () => {
        if (!canShowCharges) return;

        setChargesJumpTo(true);
        setChargesExpanded(false);
        setTab('charges');

        setTimeout(() => {
            chargesTabTopRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }, 60);
    };

    const isBlockedByOwner = getReservationDetails?.onBehalf === false;
    const isBookedByOwner = getReservationDetails?.bookerType === 'owner';

    const showItinerary =
        getReservationDetails?.module?.type === ExperienceType.Property ||
        getReservationDetails?.module?.type === ExperienceType.Boat ||
        (getReservationDetails?.module?.type === ExperienceType.Commercial &&
            (getReservationDetails?.bookingInfo?.entireVenue ||
                getReservationDetails?.bookingInfo?.meetingRooms > 0));

    const navItems: {
        key: ViewKey;
        label: string;
        mobileLabel?: string;
        visible: boolean;
    }[] = [
        { key: 'details', label: 'Reservation', visible: true },
        { key: 'itinerary', label: 'Itinerary', visible: showItinerary },
        {
            key: 'property',
            label: 'Checkin Details',
            mobileLabel: 'Checkin Details',
            visible: !isBlockedByOwner,
        },
        {
            key: 'guests',
            label: 'Guests',
            visible: canShowGuests,
        },
        {
            key: 'reservationSpaces',
            label: 'Room allocation',
            visible: shouldShowRoomAllocations,
        },
        { key: 'charges', label: 'Charges', visible: canShowCharges },
        {
            key: 'payments',
            label: 'Payments',
            visible: canShowPayments,
        },
        {
            key: 'history',
            label: 'History',
            visible: true,
        },
    ];

    const activeTab = navItems.some((n) => n.key === tab && n.visible)
        ? tab
        : 'details';

    useEffect(() => {
        if (activeTab !== 'charges') return;

        if (!hasEstimatedJobs && chargesJumpTo === undefined) {
            setChargesExpanded(true);
        }
    }, [activeTab, hasEstimatedJobs, chargesJumpTo]);

    useEffect(() => {
        if (activeTab !== 'charges') return;
        if (!chargesJumpTo) return;

        const t = setTimeout(() => setChargesJumpTo(undefined), 600);
        return () => clearTimeout(t);
    }, [activeTab, chargesJumpTo]);

    const handleNext = () => {
        if (!next) return;

        navigate(`/reservations/experience/${next}?tab=details`, {
            state: {
                name: 'viewReservation',
                component: 'reservations',
                url: 'reservations',
                index: index + 1,
                reservations: reservations,
            },
        });
    };

    const handlePrev = () => {
        if (!prev) return;

        navigate(`/reservations/experience/${prev}?tab=details`, {
            state: {
                name: 'viewReservation',
                component: 'reservations',
                url: 'reservations',
                index: index - 1,
                reservations: reservations,
            },
        });
    };

    const handleOpenItineraryItem = (bookingId: number) => {
        navigate(`/reservations/experience/${bookingId}`, {
            state: {
                name: 'viewReservation',
                component: 'details',
                url: 'reservations',
            },
        });
    };

    const handleOpenMainBooking = (bookingId: number) => {
        navigate(`/reservations/experience/${bookingId}`, {
            state: {
                name: 'viewReservation',
                component: 'details',
                url: 'reservations',
            },
        });
        setTimeout(() => {
            setTab('itinerary');
        }, 200);
    };

    const handleRemoveFromItinerary = async () => {
        const request = {
            bookerType: getReservationDetails?.bookerType,
            mainBooking: null,
        };

        const url = `/bookings/${getReservationDetails?.id}`;

        try {
            await editReservation([url, request]).unwrap();
            toast.success('Reservation has been removed from itinerary');
        } catch {
            toast.error('Failed to remove from itinerary');
        }
    };

    const filteredAddons =
        getIndividualAsset?.addOns.filter(
            (addOn: Required<AddOn>) => addOn.isAvailable,
        ) ?? [];

    const SummaryBlock = (
        <Box>
            <ReservationSummarySection
                getReservationDetails={getReservationDetails}
                getIndividualAsset={getIndividualAsset}
                loggedInUser={loggedInUser}
                startDate={startDate}
                endDate={endDate}
                calendarStart={calendarStart}
                calendarEnd={calendarEnd}
                calDescriptionActivity={calDescriptionActivity}
                calDescriptionVehicle={calDescriptionVehicle}
                calDescriptionCommercial={calDescriptionCommercial}
                calDescriptionNonVehicle={calDescriptionNonVehicle}
                fullAddress={fullAddress}
            />
            {loggedInUser?.role.includes('owner') ? (
                <Box sx={{ mt: 1.5 }}>
                    <Conflicts reservation={getReservationDetails} />
                </Box>
            ) : null}
        </Box>
    );

    const DetailsBlock =
        activeTab === 'details' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <ReservationDetailsSection
                    reservation={getReservationDetails}
                    asset={getIndividualAsset}
                    fullAddress={fullAddress}
                    shortAddress={shortAddress}
                    fullAddressDropoff={fullAddressDropoff}
                    expanded
                    onToggle={() => {}}
                    cateringJobs={cateringJobs}
                    alcoholJobs={alcoholJobs}
                    cateringSetMenuJobs={cateringSetMenuJobs}
                    alcoholSetMenuJobs={alcoholSetMenuJobs}
                    massageJobs={massageJobs}
                    onOpenChargesTab={openChargesTabFromDetails}
                    handleOpenMainBooking={handleOpenMainBooking}
                />
                {getReservationDetails?.module?.type === 'commercial' &&
                getReservationDetails?.spaces.length > 0 ? (
                    <ReservationItinerarySection
                        reservation={getReservationDetails}
                        expanded={
                            getReservationDetails?.module?.type === 'commercial'
                        }
                        onToggle={() => {}}
                    />
                ) : null}
            </Box>
        ) : null;
    const ReservationSpacesBlock =
        activeTab === 'reservationSpaces' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <ReservationSpacesSection
                    reservation={getReservationDetails}
                    experience={getIndividualAsset}
                    onEditReservation={
                        canAllocateRooms ? navigateToEditReservation : undefined
                    }
                />
            </Box>
        ) : null;

    const PropertyBlock =
        activeTab === 'property' && !isBlockedByOwner ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <ExperienceInfoSection
                    reservation={getReservationDetails}
                    asset={getIndividualAsset}
                    expanded={true}
                    onToggle={() => {}}
                />
            </Box>
        ) : null;

    const GuestsBlock =
        activeTab === 'guests' && canShowGuests ? (
            <Box>
                <ReservationGuestsSection
                    reservation={getReservationDetails}
                    asset={getIndividualAsset}
                    expanded
                    onToggle={() => {}}
                    onOpenNirovisionModal={setOpenNirovisionModal.bind(
                        null,
                        true,
                    )}
                    onOpenRsvpModal={setOpenRsvpModal.bind(null, true)}
                    isBlockedByOwner={isBlockedByOwner}
                />
            </Box>
        ) : null;

    const ItineraryBlock =
        activeTab === 'itinerary' ? (
            <Box>
                {!getReservationDetails?.mainBooking?.id ? (
                    <ItineraryTab
                        reservation={getReservationDetails}
                        handleOpenItineraryItem={handleOpenItineraryItem}
                        showItineraryForm={showItineraryForm}
                        setShowItineraryForm={setShowItineraryForm}
                        isAddOnForm={isAddOnForm}
                        setIsAddOnForm={setIsAddOnForm}
                        hasAddOns={filteredAddons.length > 0}
                        setTab={setTab}
                        originalTab={originalTab}
                        preselectedAddOn={preselectedAddOn}
                        setPreselectedAddOn={setPreselectedAddOn}
                        preselectedPartnerService={preselectedPartnerService}
                        setPreselectedPartnerService={
                            setPreselectedPartnerService
                        }
                    />
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            py: 6,
                            px: 3,
                        }}
                    >
                        <Typography
                            variant='body1'
                            sx={{
                                color: theme.palette.text.secondary,
                                textAlign: 'center',
                            }}
                        >
                            This reservation is part of{' '}
                            <Box
                                component='span'
                                sx={{ ...LinkSx, cursor: 'pointer' }}
                                onClick={() => {
                                    handleOpenMainBooking(
                                        getReservationDetails?.mainBooking?.id,
                                    );
                                }}
                            >
                                reservation{' '}
                                {`#${getReservationDetails?.mainBooking?.id} `}
                                itinerary{' '}
                            </Box>{' '}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1.5,
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                            }}
                        >
                            <Button
                                variant='outlined'
                                size='small'
                                startIcon={<OpenInNew />}
                                onClick={() => {
                                    handleOpenMainBooking(
                                        getReservationDetails?.mainBooking?.id,
                                    );
                                }}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    color: theme.palette.primary.main,
                                }}
                            >
                                Go to itinerary
                            </Button>
                            <Button
                                variant='outlined'
                                size='small'
                                color='error'
                                startIcon={<LinkOff />}
                                onClick={handleRemoveFromItinerary}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 2,
                                }}
                            >
                                Remove from itinerary
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        ) : null;

    const ChargesBlock =
        activeTab === 'charges' && canShowCharges ? (
            <Box ref={chargesTabTopRef}>
                <ReservationChargesSection
                    reservation={getReservationDetails}
                    asset={getIndividualAsset}
                    expanded={chargesExpanded}
                    onToggle={(_, isExpanded) => setChargesExpanded(isExpanded)}
                    jumpToServices={chargesJumpTo}
                />
            </Box>
        ) : null;

    const PaymentsBlock =
        activeTab === 'payments' && canShowPayments ? (
            <Box>
                <GuestPaymentHistoryAccordion
                    reservation={getReservationDetails}
                    expanded
                    onToggle={() => {}}
                />
            </Box>
        ) : null;

    const HistoryBlock =
        activeTab === 'history' ? (
            <Box>
                <ReservationHistory
                    reservation={getReservationDetails}
                    expanded
                    onToggle={() => {}}
                />
            </Box>
        ) : null;

    const assetImageSrc = getIndividualAsset?.photos?.[0]?.src;
    const showMobileThumbnail =
        isLaptop && Boolean(assetImageSrc) && isModuleAvailable;

    const actionButtonSx = {
        borderRadius: 2,
        height: 36,
        textTransform: 'none' as const,
        whiteSpace: 'nowrap' as const,
    };

    const shouldShowPrevNext = !isSubmitted && index !== undefined;

    const shouldShowDecline =
        canOwnerAdminApproveDecline &&
        getReservationDetails?.status !== 'declined';

    const hasAvailableOptionalServices = shouldShowOptionalServicesSection(
        getIndividualAsset,
        getReservationDetails,
    );

    const shouldShowEnhancements =
        (filteredAddons.length > 0 || hasAvailableOptionalServices) &&
        isGuest &&
        (isApproved || isPending);

    useEffect(() => {
        if (
            shouldShowEnhancements &&
            filteredAddons.length > 0 &&
            isSubmitted
        ) {
            setTab('itinerary');
            setShowItineraryForm(true);
            setIsAddOnForm(true);
        }
    }, [isSubmitted]);

    return (
        <>
            {isFetching === true && !assetFromProps ? (
                <Grid
                    sx={{
                        height: isLaptop ? '50vh' : isMobile ? '5px' : '50vh',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        alignContent: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <LoadingComponent />
                </Grid>
            ) : (
                <Container
                    maxWidth={isOwner ? 'xl' : 'lg'}
                    sx={{
                        px: { xs: 0.5, sm: 2, md: 0 },
                        pb: 5,
                        position: 'relative',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            justifyContent: 'space-between',
                            gap: 2,
                            flexWrap: 'wrap',
                            mb: 2,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: showMobileThumbnail
                                    ? '90px 1fr'
                                    : '1fr',
                                columnGap: 1.5,
                                rowGap: 0.75,
                                alignItems: 'start',
                                minWidth: 0,
                                flex: 1,
                            }}
                        >
                            {showMobileThumbnail ? (
                                <Box
                                    component='img'
                                    src={assetImageSrc}
                                    alt={
                                        getIndividualAsset?.name ||
                                        getReservationDetails?.module?.name ||
                                        'Experience'
                                    }
                                    sx={{
                                        width: 90,
                                        height: 90,
                                        borderRadius: 1.5,
                                        objectFit: 'cover',
                                        display: 'block',
                                        gridColumn: 1,
                                        gridRow: '1 / 3',
                                        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                    }}
                                />
                            ) : null}

                            <Typography
                                variant='h1'
                                sx={{
                                    gridColumn: showMobileThumbnail ? 2 : 1,
                                    gridRow: 1,
                                    textAlign: 'left',
                                    textTransform: 'uppercase',
                                    fontSize: {
                                        xs: '18px',
                                        sm: '22px',
                                        md: '24px',
                                    },
                                    lineHeight: { xs: 1.8, md: 1.2 },
                                    m: 0,
                                    pr: 1,
                                }}
                            >
                                {getReservationDetails?.module?.name}
                            </Typography>

                            <Box
                                sx={{
                                    gridColumn: showMobileThumbnail ? 2 : 1,
                                    gridRow: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.25,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    Reservation made on {createdAtDate}
                                </Typography>

                                {showMobileThumbnail && moduleTypeDisplay ? (
                                    <Typography
                                        component='span'
                                        variant='h6'
                                        sx={{
                                            fontSize: '.8rem',
                                            fontWeight: 600,
                                            whiteSpace: 'nowrap',
                                            alignSelf: 'flex-start',
                                            px: 1,
                                            py: 0.25,
                                            borderTopRightRadius: 8,
                                            borderBottomRightRadius: 8,
                                            backgroundColor: alpha(
                                                theme.palette.primary.main,
                                                0.15,
                                            ),
                                        }}
                                    >
                                        {moduleTypeDisplay}
                                    </Typography>
                                ) : null}
                            </Box>
                        </Box>

                        {shouldShowPrevNext ? (
                            <ButtonGroup
                                size='small'
                                variant='outlined'
                                sx={{
                                    maxHeight: '30px',
                                    alignSelf: 'flex-start',
                                    ...(isMobile
                                        ? {
                                              position: 'absolute',
                                              top: -45,
                                              right: { xs: 4, sm: 16 },
                                              zIndex: 1,
                                          }
                                        : {}),
                                }}
                            >
                                <Button
                                    startIcon={<NavigateBefore />}
                                    onClick={handlePrev}
                                    disabled={!prev}
                                >
                                    {!isMobile && 'Prev'}
                                </Button>

                                <Button
                                    endIcon={<NavigateNext />}
                                    onClick={handleNext}
                                    disabled={!next}
                                >
                                    {!isMobile && 'Next'}
                                </Button>
                            </ButtonGroup>
                        ) : null}
                    </Box>

                    <Grid container spacing={2} alignItems='flex-start'>
                        <Grid
                            item
                            xs={12}
                            md={8}
                            lg={8.5}
                            sx={{ order: { xs: 1, md: 0 } }}
                        >
                            {assetImageSrc && isModuleAvailable ? (
                                <Box
                                    sx={{
                                        mb: 2,
                                        display: showMobileThumbnail
                                            ? 'none'
                                            : 'block',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                        backgroundColor:
                                            theme.palette.neutral.main,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            height: {
                                                xs: 160,
                                                sm: 200,
                                                md: 240,
                                            },
                                            width: '100%',
                                        }}
                                    >
                                        <Box
                                            component='img'
                                            src={assetImageSrc}
                                            alt={
                                                getIndividualAsset?.name ||
                                                getReservationDetails?.module
                                                    ?.name
                                            }
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                display: 'block',
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                inset: 0,
                                                background:
                                                    'linear-gradient(to top, rgba(0,0,0,0.45), rgba(0,0,0,0) 55%)',
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                left: 16,
                                                right: 16,
                                                bottom: 12,
                                                display: 'flex',
                                                alignItems: 'flex-end',
                                                justifyContent: 'space-between',
                                                gap: 1.5,
                                            }}
                                        >
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography
                                                    sx={{
                                                        color: '#fff',
                                                        fontWeight: 900,
                                                        fontSize: 14,
                                                        lineHeight: 1.2,
                                                        textShadow:
                                                            '0 1px 2px rgba(0,0,0,0.35)',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow:
                                                            'ellipsis',
                                                    }}
                                                >
                                                    {camelCaseToTitleCase(
                                                        getIndividualAsset?.type,
                                                    )}
                                                </Typography>
                                                {getIndividualAsset?.suburb ? (
                                                    <Typography
                                                        sx={{
                                                            mt: 0.25,
                                                            color: 'rgba(255,255,255,0.9)',
                                                            fontWeight: 700,
                                                            fontSize: 12,
                                                            lineHeight: 1.2,
                                                            textShadow:
                                                                '0 1px 2px rgba(0,0,0,0.3)',
                                                            whiteSpace:
                                                                'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow:
                                                                'ellipsis',
                                                        }}
                                                    >
                                                        {
                                                            getIndividualAsset?.suburb
                                                        }
                                                        {getIndividualAsset?.state
                                                            ? `, ${getIndividualAsset?.state}`
                                                            : ''}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            ) : null}

                            <Box
                                sx={{
                                    position: 'sticky',
                                    top: isNavSelectLayout
                                        ? { xs: 53, md: 12 }
                                        : { xs: 64, md: 12 },
                                    zIndex: 6,
                                    backgroundColor: isNavSelectLayout
                                        ? theme.palette.form.background
                                        : theme.palette.neutral.main,
                                    borderRadius: 2,
                                    mb: 2,
                                    ...(isNavSelectLayout
                                        ? { p: 2, pt: 2.5 }
                                        : { py: 1 }),
                                }}
                            >
                                {isNavSelectLayout ? (
                                    <TextField
                                        select
                                        fullWidth
                                        size='small'
                                        label='View'
                                        value={activeTab}
                                        onChange={(e) => {
                                            const v = e.target.value as ViewKey;
                                            setTab(v);
                                            if (v === 'charges') {
                                                setChargesJumpTo(undefined);
                                            }
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                        SelectProps={{
                                            displayEmpty: false,
                                            MenuProps: {
                                                anchorOrigin: {
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                },
                                                transformOrigin: {
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                },
                                                PaperProps: {
                                                    sx: {
                                                        maxHeight: 360,
                                                    },
                                                },
                                            },
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                backgroundColor:
                                                    theme.palette.neutral.main,
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontWeight: 600,
                                            },
                                            '& .MuiSelect-select': {
                                                fontWeight: 700,
                                                fontSize: 14,
                                            },
                                        }}
                                    >
                                        {navItems
                                            .filter((n) => n.visible)
                                            .map((n) => (
                                                <MenuItem
                                                    key={n.key}
                                                    value={n.key}
                                                >
                                                    {n.label}
                                                </MenuItem>
                                            ))}
                                    </TextField>
                                ) : (
                                    <Tabs
                                        value={activeTab}
                                        onChange={(_, v) => {
                                            setTab(v);
                                            if (v === 'charges') {
                                                setChargesJumpTo(undefined);
                                            }
                                        }}
                                        variant='standard'
                                        scrollButtons={false}
                                        sx={{
                                            minHeight: 40,
                                            '& .MuiTabs-flexContainer': {
                                                flexWrap: 'wrap',
                                            },
                                            '& .MuiTab-root': {
                                                minHeight: 40,
                                                px: 1.25,
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                fontSize: 13,
                                                color: theme.palette.text
                                                    .secondary,
                                            },
                                            '& .MuiTab-root.Mui-selected': {
                                                color: theme.palette.text
                                                    .primary,
                                            },
                                            '& .MuiTabs-indicator': {
                                                height: 3,
                                                borderRadius: 3,
                                            },
                                        }}
                                    >
                                        {navItems
                                            .filter((n) => n.visible)
                                            .map((n) => (
                                                <Tab
                                                    key={n.key}
                                                    value={n.key}
                                                    label={n.label}
                                                />
                                            ))}
                                    </Tabs>
                                )}
                            </Box>

                            <Paper
                                elevation={0}
                                square
                                sx={{
                                    backgroundColor: theme.palette.neutral.main,
                                    boxShadow: 'none',
                                    borderRadius: 2,
                                    mb: 2,
                                }}
                            >
                                {DetailsBlock}
                                {PropertyBlock}
                                {GuestsBlock}
                                {ItineraryBlock}
                                {ReservationSpacesBlock}
                                {ChargesBlock}
                                {PaymentsBlock}
                                {HistoryBlock}
                            </Paper>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            md={4}
                            lg={3.5}
                            sx={{ order: { xs: 0, md: 1 } }}
                        >
                            <Paper
                                elevation={3}
                                sx={{
                                    backgroundColor: 'neutral.main',
                                    overflow: 'hidden',
                                    position: { xs: 'static', md: 'sticky' },
                                    top: { md: 12 },
                                    zIndex: 4,
                                }}
                            >
                                <Box
                                    sx={{
                                        px: 2.25,
                                        pt: 2,
                                        pb: 2.5,
                                        backgroundColor:
                                            theme.palette.form.background,
                                        borderBottom: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                    }}
                                >
                                    {SummaryBlock}
                                </Box>
                                {hasPrimaryActions ? (
                                    <Box sx={{ mt: 1.75, px: 2 }}>
                                        <Box sx={{ display: 'grid', gap: 1 }}>
                                            {canOwnerAdminApproveDecline ? (
                                                <>
                                                    <Button
                                                        variant='contained'
                                                        sx={{
                                                            ...actionButtonSx,
                                                            height: 38,
                                                        }}
                                                        disabled={
                                                            disableApprove ||
                                                            loggedInUser?.viewOnly
                                                        }
                                                        onClick={() =>
                                                            onClickApprove()
                                                        }
                                                    >
                                                        Approve
                                                    </Button>

                                                    <Box
                                                        sx={{
                                                            display: 'grid',
                                                            gridTemplateColumns:
                                                                {
                                                                    xs: '1fr',
                                                                    sm: '1fr 1fr',
                                                                },
                                                            gap: 1,
                                                        }}
                                                    >
                                                        {canEdit ? (
                                                            <Button
                                                                variant='outlined'
                                                                sx={{
                                                                    ...actionButtonSx,
                                                                    height: 34,
                                                                }}
                                                                disabled={
                                                                    loggedInUser?.viewOnly
                                                                }
                                                                onClick={
                                                                    navigateToEditReservation
                                                                }
                                                            >
                                                                Edit
                                                            </Button>
                                                        ) : (
                                                            <Box />
                                                        )}

                                                        {shouldShowDecline ? (
                                                            <Button
                                                                variant='outlined'
                                                                color='primary'
                                                                sx={{
                                                                    ...actionButtonSx,
                                                                    height: 34,
                                                                }}
                                                                onClick={
                                                                    onClickOpenDeclineModal
                                                                }
                                                                disabled={
                                                                    loggedInUser?.viewOnly
                                                                }
                                                            >
                                                                Decline
                                                            </Button>
                                                        ) : (
                                                            <Box />
                                                        )}
                                                    </Box>
                                                </>
                                            ) : null}

                                            {isAttending && (
                                                <Typography variant='body3'>
                                                    <span
                                                        style={{
                                                            fontWeight: '600',
                                                        }}
                                                    >
                                                        {' '}
                                                        Note:{' '}
                                                    </span>
                                                    you have been added to this
                                                    reservation as an attendee.
                                                    You cannot edit or cancel
                                                    this reservation.
                                                </Typography>
                                            )}

                                            {canOwnerAdminApproveDecline ? null : canEdit ? (
                                                <Button
                                                    color='primary'
                                                    variant='outlined'
                                                    sx={{
                                                        ...actionButtonSx,
                                                        height: 34,
                                                    }}
                                                    disabled={
                                                        loggedInUser?.viewOnly ||
                                                        isAttending
                                                    }
                                                    onClick={
                                                        navigateToEditReservation
                                                    }
                                                >
                                                    Edit
                                                </Button>
                                            ) : null}

                                            {canGuestCancelRequest ? (
                                                <Button
                                                    color='primary'
                                                    variant='outlined'
                                                    sx={{
                                                        ...actionButtonSx,
                                                        height: 34,
                                                    }}
                                                    disabled={
                                                        loggedInUser?.viewOnly ||
                                                        isAttending
                                                    }
                                                    onClick={onClickOpenModal}
                                                >
                                                    Cancel Request
                                                </Button>
                                            ) : null}

                                            {canOwnerCancelCompleted ||
                                            canCancelApproved ? (
                                                <Button
                                                    variant='outlined'
                                                    color='primary'
                                                    sx={{
                                                        ...actionButtonSx,
                                                        height: 34,
                                                    }}
                                                    disabled={
                                                        loggedInUser?.viewOnly ||
                                                        isAttending
                                                    }
                                                    onClick={onClickOpenModal}
                                                >
                                                    Cancel Reservation
                                                </Button>
                                            ) : null}
                                        </Box>
                                    </Box>
                                ) : null}

                                {getReservationDetails?.rsvpConfig
                                    ?.rsvpEnabled &&
                                    ![
                                        getReservationDetails?.booker?.id,
                                        getReservationDetails?.createdBy?.id,
                                    ].includes(loggedInUser?.sub) &&
                                    loggedInAttendee && (
                                        <RsvpActions
                                            bookingId={
                                                getReservationDetails?.id
                                            }
                                            attendee={loggedInAttendee}
                                            isApproved={isApproved}
                                        />
                                    )}

                                <Box sx={{ p: 2 }}>
                                    {loggedInUser?.role.includes('owner') ? (
                                        <>
                                            {!isBlockedByOwner &&
                                                !isBookedByOwner && (
                                                    <ReservationNotesAccordion
                                                        reservation={
                                                            getReservationDetails
                                                        }
                                                    />
                                                )}

                                            <Box sx={{ mt: 2 }}>
                                                <ReservationAttachmentsCard
                                                    bookingId={
                                                        getReservationDetails?.id
                                                    }
                                                />
                                            </Box>

                                            {loggedInUser?.role.includes(
                                                'owner',
                                            ) ||
                                            loggedInUser?.role === 'admin' ? (
                                                <Box sx={{ mt: 2 }}>
                                                    <ReservationHostsContractors
                                                        booking={
                                                            getReservationDetails
                                                        }
                                                    />
                                                </Box>
                                            ) : null}

                                            {(loggedInUser?.role.includes(
                                                'owner',
                                            ) ||
                                                loggedInUser?.role ===
                                                    'admin') &&
                                            getReservationDetails?.bookerType !==
                                                'owner' ? (
                                                <Box sx={{ mt: 2 }}>
                                                    <ReservationExpenseClaims
                                                        bookingId={
                                                            getReservationDetails?.id
                                                        }
                                                        expenses={expenseClaims}
                                                    />
                                                </Box>
                                            ) : null}
                                        </>
                                    ) : null}
                                </Box>
                                {shouldShowEnhancements && (
                                    <AddOnSection
                                        experience={getIndividualAsset}
                                        hasAddOns={filteredAddons.length > 0}
                                        hasOptionalServices={
                                            hasAvailableOptionalServices
                                        }
                                        setShowItineraryForm={
                                            setShowItineraryForm
                                        }
                                        setIsAddOnForm={setIsAddOnForm}
                                        setTab={setTab}
                                        tab={tab}
                                        originalTab={originalTab}
                                        setOriginalTab={setOriginalTab}
                                        setPreselectedAddOn={
                                            setPreselectedAddOn
                                        }
                                        setPreselectedPartnerService={
                                            setPreselectedPartnerService
                                        }
                                    />
                                )}

                                {loggedInUser?.viewOnly ? (
                                    <Box sx={{ mt: 2 }}>
                                        <Alert
                                            severity='error'
                                            sx={{ borderRadius: 2 }}
                                        >
                                            You do not have permission to edit
                                            this reservation. Please contact
                                            your administrator.
                                        </Alert>
                                    </Box>
                                ) : null}

                                {!isMobile && canShowContactForm ? (
                                    <Box
                                        sx={{
                                            mt: 2,
                                            p: 1.75,
                                            borderRadius: 2,
                                            backgroundColor:
                                                theme.palette.neutral.main,
                                        }}
                                    >
                                        {!(
                                            loggedInUser?.role?.includes(
                                                'owner',
                                            ) &&
                                            !getReservationDetails?.owner?.messagingUsers?.includes(
                                                loggedInUser.sub,
                                            )
                                        ) ? (
                                            <ContactForm
                                                reservation={
                                                    getReservationDetails
                                                }
                                            />
                                        ) : null}
                                    </Box>
                                ) : null}
                            </Paper>
                        </Grid>
                    </Grid>

                    {isMobile && canShowContactForm ? (
                        <Box
                            sx={{
                                mt: 3,
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                backgroundColor: theme.palette.form.background,
                            }}
                        >
                            {!(
                                loggedInUser?.role?.includes('owner') &&
                                !getReservationDetails?.owner?.messagingUsers?.includes(
                                    loggedInUser.sub,
                                )
                            ) ? (
                                <ContactForm
                                    reservation={getReservationDetails}
                                />
                            ) : null}
                        </Box>
                    ) : null}

                    <RsvpModal
                        config={getReservationDetails?.rsvpConfig}
                        attendees={
                            getReservationDetails?.staffAttending &&
                            getReservationDetails?.externalAttendees
                                ? [
                                      ...getReservationDetails.staffAttending,
                                      ...getReservationDetails.externalAttendees,
                                  ]
                                : []
                        }
                        open={openRsvpModal}
                        onClose={() => setOpenRsvpModal(false)}
                        bookingId={getReservationDetails?.id}
                        reservationStatus={getReservationDetails?.status}
                    />

                    <NirovisonAccessModal
                        open={openNirovisionModal}
                        onClose={handleCloseNirovisionModal}
                        initialTab={
                            shouldOpenAccessRequests ? 'requests' : undefined
                        }
                        bookingId={getReservationDetails?.id}
                        bookerType={getReservationDetails?.bookerType}
                        removeAccessChecked={
                            getReservationDetails?.removeNirovisionAccess
                        }
                        accessRemoved={
                            getReservationDetails?.nirovisionAccessRemovedAt
                        }
                        reservationStartDate={getReservationDetails?.startDate}
                        reservationEndDate={getReservationDetails?.endDate}
                        reservationStatus={getReservationDetails?.status}
                        usesVehicleRec={
                            getIndividualAsset?.nirovisionVehicleLabelId ||
                            getIndividualAsset?.details
                                ?.dropoffNirovisionVehicleLabelId
                        }
                    />

                    {!isSubmitted ? (
                        <DeclineReservationModal
                            open={openDeclineModal}
                            onClose={handleCloseDeclineModal}
                            onChange={handleChangeDeclineModal}
                            message={
                                'To decline this reservation, please select a reason.'
                            }
                            component={'reservation'}
                            id={getReservationDetails?.id}
                        />
                    ) : null}

                    <CancelReservationModal
                        open={open}
                        onClose={handleCloseModal}
                        onChange={handleCloseModal}
                        message='Cancel this reservation'
                        component='reservation'
                        id={getReservationDetails?.id}
                    />
                </Container>
            )}
        </>
    );
}
